import { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { Card } from '../styles/components';
import { MultiSearchableSelect } from './SearchableSelect';
import { CascadingSelect } from './CascadingSelect';
import { TimeRangeSlider } from './TimeRangeSlider';
import type { FilterState, FilterOptions } from '../types';
import { industryLabel, stateLabel, statusLabel, useT } from '../i18n';

const DAY_MS = 86_400_000;

// Phone breakpoint for the bottom-sheet treatment. A phone rotated to
// landscape keeps a width >600px but a very short height, so `max-width: 600px`
// alone switched the sheet off on rotation and the panel reverted to the
// full inline filter card that swamped the short viewport. Also treating any
// short landscape viewport as a phone keeps the sheet across rotation; tablets
// in landscape (height ≥768px) are excluded by the max-height clause.
const PHONE = '@media (max-width: 600px), (max-height: 600px) and (orientation: landscape)';

const FilterCard = styled(Card)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  /* Pin to the top while the dashboard scrolls underneath. The .tile
     background is opaque and z-index keeps it (and its open dropdowns)
     above the charts/map/table. align-self:start stops the grid from
     stretching the item, which would defeat sticky. The widget caps all
     page-level z-indexes at 9 so the host site's nav can stack above us. */
  position: sticky;
  top: var(--sticky-below-nav);
  align-self: start;
  z-index: 5;

  /* Match budget's filter card: --shadow-m reads more present than the
     faint tile--elevation-low shadow-s default. */
  box-shadow: var(--shadow-m);

  /* On phones/tablets the card is a tall stacked block, not the short single
     row it is on desktop. Pinned (z-index 5) it would float over the map
     (deliberately isolated at z-index 0) and the table as they scroll under
     it, hiding most of the map. Drop to static so it scrolls inline here.
     Mirrors the budget widget's sticky sidebar reset. */
  @media (max-width: 1024px) {
    position: static;
    top: auto;
  }

  /* Phone: the panel becomes a bottom sheet summoned by the Filter button, so
     the dashboard opens on the data instead of a full screen of controls. It's
     pinned to the bottom of the viewport, slides up when [data-open], and the
     header/footer chrome below become visible. Off-viewport + pointer-events
     none when closed so it never blocks the page. */
  ${PHONE} {
    position: fixed;
    inset: auto 0 0 0;
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: 0;
    margin: 0;
    padding: 0;
    max-height: 88vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: var(--radius-l) var(--radius-l) 0 0;
    z-index: 1100;
    box-shadow: 0 -6px 24px rgba(24, 28, 68, 0.22);
    transform: translateY(100%);
    transition: transform 0.28s ease;
    pointer-events: none;

    &[data-open='true'] {
      transform: translateY(0);
      pointer-events: auto;
    }
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr repeat(4, 1fr);
  gap: ${theme.spacing.md};
  flex: 1;
  min-width: 0;

  /* Five tracks only fit on wide embeds. As the widget narrows, drop to two
     columns (tablet) and then one (phone) so the dropdown labels and the date
     slider's header stop overlapping each other. The date slider — always the
     first child — spans the full row whenever the grid wraps, since its
     header (label on the left, date range on the right) needs the width to
     lay out on one line. */
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    & > :first-child { grid-column: 1 / -1; }
  }
  ${PHONE} {
    grid-template-columns: 1fr;
    /* The sheet body owns the padding now that FilterCard is edge-to-edge. */
    padding: var(--space-4) var(--space-5);
  }
`;

// ── Mobile bottom-sheet chrome ──────────────────────────────────────────────
// All of the following render in the DOM at every width but are display:none
// above 600px, so desktop/tablet keep the inline FilterCard untouched.

// The summon button that replaces the always-open panel on phones.
const MobileTriggerBar = styled.div`
  display: none;
  ${PHONE} {
    display: block;
  }
`;

const MobileTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: var(--space-3) var(--space-4);
  font-family: var(--sans-stack);
  font-size: ${scaled(15)};
  font-weight: ${theme.fontWeight.medium};
  color: var(--color-text-default);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-bg-muted);
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-s);
  cursor: pointer;

  svg { width: 18px; height: 18px; }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--primary-color);
  color: #fff;
  font-size: ${scaled(12)};
  font-weight: ${theme.fontWeight.semibold};
`;

// Dimmed backdrop behind the open sheet; tap to dismiss.
const Scrim = styled.div`
  display: none;
  ${PHONE} {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1099;
    background: rgba(24, 28, 68, 0.4);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.28s ease;

    &[data-open='true'] {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

// Sticky header inside the sheet — title + close, stays put while the body
// scrolls.
const SheetTopBar = styled.div`
  display: none;
  ${PHONE} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 1;
    padding: var(--space-4) var(--space-5);
    background: var(--color-bg-surface);
    border-bottom: 1px solid var(--color-bg-muted);
  }
`;

const SheetTitle = styled.span`
  font-family: var(--sans-stack);
  font-size: ${scaled(18)};
  font-weight: ${theme.fontWeight.medium};
  color: var(--color-text-default);
`;

const SheetCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: var(--radius-m);
  font-size: ${scaled(24)};
  line-height: 1;
  color: var(--color-text-secondary);
  cursor: pointer;

  &:hover { background: var(--color-bg-muted); }
`;

// Sticky footer — reset + done, always reachable above the keyboard/scroll.
const SheetFooter = styled.div`
  display: none;
  ${PHONE} {
    display: flex;
    gap: ${theme.spacing.sm};
    position: sticky;
    bottom: 0;
    /* Explicit z-index so this opaque sticky bar stays above closed select
       pills (Container z-index 2 — DOM order then wins, footer paints over)
       but BELOW an OPEN select (Container z-index 3), so an open dropdown's
       suggestion list isn't hidden behind the bar. Without it, iOS Safari's
       sticky stacking quirk paints the footer over the open dropdown. */
    z-index: 2;
    padding: var(--space-4) var(--space-5);
    background: var(--color-bg-surface);
    border-top: 1px solid var(--color-bg-muted);
  }
`;

const SheetResetButton = styled.button`
  flex: 0 0 auto;
  padding: var(--space-3) var(--space-5);
  font-family: var(--sans-stack);
  font-size: ${scaled(15)};
  font-weight: ${theme.fontWeight.medium};
  color: var(--color-text-secondary);
  background: none;
  border: 1px solid var(--color-bg-muted);
  border-radius: var(--radius-m);
  cursor: pointer;
`;

const SheetDoneButton = styled.button`
  flex: 1;
  padding: var(--space-3) var(--space-5);
  font-family: var(--sans-stack);
  font-size: ${scaled(15)};
  font-weight: ${theme.fontWeight.semibold};
  color: #fff;
  background: var(--primary-color);
  border: none;
  border-radius: var(--radius-m);
  cursor: pointer;
`;

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}


interface FilterPanelProps {
  filters: FilterState;
  filterOptions: FilterOptions;
  minTimestamp: number;
  maxTimestamp: number;
  fromTimestamp: number;
  toTimestamp: number;
  onFiltersChange: (filters: FilterState) => void;
  onTimeChange: (from: number, to: number) => void;
  /** Kept on the prop surface for symmetry with the App flow; the reset
      affordance now lives on the ActiveFilterChips bar instead of inside
      the card, since it should fire on chart-click / date selections too —
      not only the panel filters. */
  onClearAll: () => void;
}

export function FilterPanel({
  filters,
  filterOptions,
  minTimestamp,
  maxTimestamp,
  fromTimestamp,
  toTimestamp,
  onFiltersChange,
  onTimeChange,
  onClearAll,
}: FilterPanelProps) {
  const m = useT();
  const update = (key: keyof FilterState, value: string[]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  // Mobile bottom-sheet open state (ignored above 600px, where the trigger is
  // hidden and the panel renders inline).
  const [sheetOpen, setSheetOpen] = useState(false);

  // While the sheet is open: lock background scroll and close on Escape.
  useEffect(() => {
    if (!sheetOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheetOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [sheetOpen]);

  // Badge count = one per active selection (mirrors the ActiveFilterChips), plus
  // one for a narrowed date range.
  const dateActive = fromTimestamp - minTimestamp > DAY_MS || maxTimestamp - toTimestamp > DAY_MS;
  const activeCount =
    filters.industries.length +
    filters.federalStates.length +
    filters.investmentStatuses.length +
    filters.companies.length +
    (dateActive ? 1 : 0);

  return (
    <>
      <MobileTriggerBar>
        <MobileTriggerButton
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
        >
          <FilterIcon />
          {m.filterPanel.filter}
          {activeCount > 0 && <FilterBadge>{activeCount}</FilterBadge>}
        </MobileTriggerButton>
      </MobileTriggerBar>

      <Scrim data-open={sheetOpen} onClick={() => setSheetOpen(false)} aria-hidden="true" />

      <FilterCard data-open={sheetOpen}>
        <SheetTopBar>
          <SheetTitle>{m.filterPanel.filter}</SheetTitle>
          <SheetCloseButton type="button" onClick={() => setSheetOpen(false)} aria-label={m.filterPanel.closeFilter}>
            ×
          </SheetCloseButton>
        </SheetTopBar>
        <FilterGrid>
        <TimeRangeSlider
          minTimestamp={minTimestamp}
          maxTimestamp={maxTimestamp}
          fromTimestamp={fromTimestamp}
          toTimestamp={toTimestamp}
          onChange={onTimeChange}
        />
        <CascadingSelect
          tree={filterOptions.industryTreeAll ?? filterOptions.industryTree}
          value={filters.industries}
          onChange={v => update('industries', v)}
          placeholder={m.filterPanel.allIndustries}
          label={m.filterPanel.industry}
          formatLabel={industryLabel}
        />
        <MultiSearchableSelect
          label={m.filterPanel.federalState}
          placeholder={m.filterPanel.allFederalStates}
          options={filterOptions.federalStates}
          formatLabel={stateLabel}
          value={filters.federalStates}
          onChange={v => update('federalStates', v)}
        />
        <MultiSearchableSelect
          label={m.filterPanel.status}
          placeholder={m.filterPanel.allStatuses}
          options={filterOptions.investmentStatuses}
          formatLabel={statusLabel}
          value={filters.investmentStatuses}
          onChange={v => update('investmentStatuses', v)}
        />
        <MultiSearchableSelect
          label={m.filterPanel.company}
          placeholder={m.filterPanel.allCompanies}
          options={filterOptions.companies}
          value={filters.companies}
          onChange={v => update('companies', v)}
        />
      </FilterGrid>
      <SheetFooter>
        <SheetResetButton type="button" onClick={onClearAll}>{m.filterPanel.reset}</SheetResetButton>
        <SheetDoneButton type="button" onClick={() => setSheetOpen(false)}>{m.filterPanel.done}</SheetDoneButton>
      </SheetFooter>
    </FilterCard>
    </>
  );
}
